import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import ScorePane from './graph/scorePane';
import clsx from 'clsx';
import { Fade } from "react-reveal";
import ReactCursorPosition from 'react-cursor-position';
import BackgroundLines from './graph/bglines';
import CoreAnalysisLineBarGraph from './graph/linebar';
import CoreAnalysisBarGraph from './graph/bar';
import AgendaSection from './notes/agendaSection';
import MarkerSection from './notes/markerSection';
import PillSection from './pills';
import { enableHorizontalScroll } from '../../../../../../helper/hooks/enableHorizontalScroll';
import useWindowSize from '../../../../../../helper/hooks/useWindowSize';
import UtilService from '../../../../../../helper/service';
import { useDispatch, useSelector } from 'react-redux';
import { updateOnBoardingSettings } from '../../../../../../store/actions/creator/creatorOnBoarding';

const useStyles = makeStyles(() => ({
  container: {
    flex: 1,
    width: '100%',
    display: 'flex',
    paddingTop: 49,
    marginLeft: -310,
  },
  graphSection: {
    flex: 1,
    flexDirection: 'column',
    display: 'flex',
    position: 'relative',
    alignItems: 'center',
    overflowX: 'hidden',
    marginRight: -310,
  },
  graphPane: {
    position: 'relative',
    height: '100%',
  },
  customBottomline: {
    position: 'absolute',
    height: 4,
    width: '4000%',
    background: '#8192A2',
    bottom: 24
  },
  customTopline: {
    position: 'absolute',
    height: 4,
    width: '4000%',
    background: '#8192A2',
    top: 36
  },
  customTopTitle: {
    top: 33.5,
    position: 'absolute',
    color: 'red',
    fontSize: 23,
    fontFamily: 'Spartan-bold',
    left: 8,
    '& p': {
      position: 'fixed'
    },
    '& p:nth-child(2)': {
      marginTop: 46,
      fontSize: 15,
      fontFamily: 'Spartan-semibold',
      textTransform: 'uppercase',
      letterSpacing: 1.8
    }
  },
  reloadBtn: {
    color: '#017AEF',
    width: 70,
    height: 70
  },
  fixedPane: {
    position: 'fixed',
    zIndex: 5,
    background: '#F0F0F0',
    marginTop: 0,
  },
  fixedScorePane: {
    position: 'fixed',
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    background: '#f0f0f0',
    zIndex: 5,
  },
  videoBlueLine: {
    width: 2,
    height: '100%',
    background: '#0075FF',
    position: 'absolute',
    left: 30,
    transition: '1.5s linear',
    zIndex: 12,
  },
  pane: {
    position: 'absolute',
    marginTop: 50,
  },
  video: {
    width: 1,
    height: 135,
    marginTop: -85,
    marginBottom: -100
  },
  time: {
    color: '#017AEF',
    fontSize: 17,
    fontFamily: 'Spartan-extrabold',
    background: '#F0F0F0',
    width: 38,
    height: 18,
    paddingTop: 2,
    marginLeft: -18,
    marginTop: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute'
  },
  resize: {
    width: 16,
    position: 'relative',
    marginBottom: -10,
    marginTop: -17
  }
}));

function getResponsiveWidth(width: any) {
  let customWidth = 600
  if (width > 319 ) { customWidth = width - 150 }
  if (width > 500 ) { customWidth = width - 200 }
  if (width > 600 ) { customWidth = width - 200 }
  if (width > 700 ) { customWidth = width - 250 }
  if (width > 900 ) { customWidth = width - 420 }
  if (width > 1200 ) { customWidth = width - 600 }
  return customWidth
}

export default function Module4Slide2GraphArea(props: any) {

  const classes = useStyles();
  const { width } = useWindowSize();
  const { module4Settings, userPreferences } = useSelector((state: any) => state.creatorOnBoarding);
  const { module4Data } = userPreferences;
  const { session, analyze } = module4Data.value;
  const dispatch = useDispatch();

  const { graphZoom, videoPlayed, videoPopIn, graphType, addNoteX, addNoteY, filterList } = module4Settings;
  const scrollRef: any = useRef();
  const scrollAllPartRef: any = useRef();
  const pencilRef: any = useRef();
  const stimulus = analyze.stimulus;
  const { session_type, session_status, session_trim_from, session_trim_to, scheduled_end_at, started_at, ended_at } = session;
  const analysed_end = Math.floor(ended_at ? ended_at : scheduled_end_at);
  const analyseStart = session_trim_from ? Number(session_trim_from) : Number(started_at)
  const analyseEnd = session_trim_to ? Number(session_trim_to) : analysed_end
  const all: any = [];
  const paneWidth = getResponsiveWidth(width);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { onPillClicked } = props;

  const graphLength = useMemo(() => {
    if (stimulus && session_type === 'ASYNC') return stimulus.stimulus_video_length;
    return analyseEnd - analyseStart
  }, [stimulus, analyseEnd, analyseStart]);

  const videoStart = 0;
  const getMaxWidth = Math.floor(graphLength / Math.pow(graphZoom, 2) * 15);
  const clipRatio = stimulus?.stimulus_video_length / graphLength
  const isbluelineWithoutStimulus = !!videoPopIn && !videoPlayed;

  const blueLineWidth = isbluelineWithoutStimulus
    ? ((videoPopIn - videoStart) * 15 / Math.pow(graphZoom, 2))
    : (videoPlayed - (videoStart / (stimulus ? stimulus.stimulus_video_length : graphLength))) * clipRatio * getMaxWidth || 0

  const blueLineLabel = isbluelineWithoutStimulus
    ? UtilService.getTimeLabel(videoPopIn, 1, 0)
    : UtilService.getTimeLabel(videoPlayed * (stimulus ? stimulus.stimulus_video_length : graphLength), 1, 0)

  useEffect(() => {
    if (analyseEnd && graphLength) {
      dispatch(updateOnBoardingSettings('graphZoom', Math.sqrt(graphLength * 15 / paneWidth)))
    }
  }, [analyseEnd, width, stimulus]);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      if (!scrollRef.current) return;
      enableHorizontalScroll(scrollRef);
    }, 100)
    return () => clearTimeout(timeOut);
  }, [scrollRef, scrollAllPartRef])

  const onContainerMouseMove = useCallback((event: any) => {
    if (!scrollRef.current) return
    const boundingRect = scrollRef.current.getBoundingClientRect();
    if (!pencilRef?.current) return;
    pencilRef.current.setMouseXPosition(Math.floor(Number(event.clientX)) - Math.floor(Number(boundingRect.left)));

    const commentContainer = document.getElementById("comment_container");
    if (!commentContainer) return;
    const commentBoundingRect = commentContainer.getBoundingClientRect();
    pencilRef.current.setMouseYPosition(Math.floor(Number(event.clientY)) - Math.floor(Number(commentBoundingRect.top)));

  }, [scrollRef]);

  const onZoom = (length: any, start: any) => {
    const zooming = Math.sqrt(length * 15 / paneWidth);
    const graphStart = (stimulus && session_type === 'ASYNC') ? 0 : analyseStart;

    if (zooming < 1) {
      dispatch(updateOnBoardingSettings('graphZoom', 1 ))
    } else {
      dispatch(updateOnBoardingSettings('graphZoom', zooming ))
    }
    dispatch(updateOnBoardingSettings('xScroll', (start - graphStart) * 15 / Math.pow(zooming < 1 ? 1 : zooming, 2) ))
  };

  const onScroll = (e: any) => {
    if (!scrollAllPartRef && !scrollAllPartRef.current && !scrollAllPartRef.current.scrollLeft) {
      return false;
    }
    if (!scrollAllPartRef.current) return false;
    scrollAllPartRef.current.scrollLeft = e.target.scrollLeft
  }

  const onSetVideoPlayed = (e: number) => {
    dispatch(updateOnBoardingSettings('videoPopIn', e))
    dispatch(updateOnBoardingSettings('clickedPillsTime', new Date().getTime()))
  }

  const participants = analyze.participant_analyze
    .reduce((prev: any[], { }, index: number) => {
      const key = Object.keys(analyze.participant_analyze[index])[0];
      key && prev.push({
        activeParticipant: key,
        immersion: analyze.participant_analyze[index][key]?.immersion,
        pills: analyze.participant_analyze[index][key]?.pills,
        safety: analyze.participant_analyze[index][key]?.psych_readiness,
        overall_safety: analyze.participant_analyze[index][key]?.safety_index
      })
      return prev
    }, []);

  const graphs: any = [
    {
      activeParticipant: 'All',
      immersion: analyze.immersion_index,
      pills: analyze.pills,
      safety: analyze.psych_readiness,
      overall_safety: analyze.overall_safety
    },
    ...participants
  ].filter((item: any) => item.immersion.length !== 0 && filterList.includes(item.activeParticipant))

  return (
    <>
      <div
        className={classes.pane}
        style={{ marginTop: stimulus ? (isMobile ? 106 + (width - 30) * 135 / 240 : 56) : (isMobile ? 85 : 35) }}
      >
        {!isMobile && graphs[0] && graphs[0].activeParticipant === 'All' && <div>
          <div className={classes.fixedScorePane} style={{ marginTop: stimulus ? -110 : -41 }}>
            <div style={{
              width: '100%',
              height: stimulus ? 110 : 40,
              background: '#f0f0f0',
            }} />
            {graphType !== 1 && <ScorePane
              sessionStatus={session_status}
              immersionData={graphs[0].immersion}
              participant={graphs[0].activeParticipant}
              graphWidth={paneWidth}
              marginBottom={0.1}
            />}
            {graphType === 1 && <ScorePane
              sessionStatus={session_status}
              participant={graphs[0].activeParticipant}
              safetyOverall={graphs[0].overall_safety}
              graphWidth={paneWidth}
              marginBottom={-1}
            />}
          </div></div>}
        {graphs.map((data: any, index: number) => (
          <Fade key={index}>
            <div>
              {graphType !== 1 && <ScorePane
                sessionStatus={session_status}
                immersionData={data.immersion}
                participant={data.activeParticipant}
                graphWidth={paneWidth}
              />}
              {graphType !== 0 && <ScorePane
                sessionStatus={session_status}
                participant={data.activeParticipant}
                safetyOverall={data.overall_safety}
                graphWidth={paneWidth}
              />}
            </div>
          </Fade>
        ))}
      </div>

      <div
        className={clsx(classes.graphPane, 'custom_scroll')}
        id={"div_hor_scroll"}
        style={{ width: paneWidth, marginTop: (stimulus) ? 0 : 0 }} // 50 removed
        onMouseMove={(e: any) => onContainerMouseMove(e)}
        ref={scrollRef}
        onScroll={onScroll}
      >

        <div
          className={classes.videoBlueLine}
          style={{ left: blueLineWidth }}
        >
          <div 
            className={classes.time}
            style={{marginTop: stimulus ? 30 : 10}}
          >
            {blueLineLabel}
          </div>
        </div>

        <ReactCursorPosition
          hoverDelayInMs={250}
          hoverOffDelayInMs={150}
        >

          <div style={{ width: getMaxWidth, marginTop: all.length > 0 ? 35 : 60 }}>
            <BackgroundLines
              getMaxWidth={getMaxWidth}
              paneWidth={paneWidth}
            />

            {!isMobile && graphs[0] && graphs[0].activeParticipant === 'All' &&
              <div
                ref={scrollAllPartRef}
                className={clsx('custom_scroll2', classes.fixedPane)}
                style={{ width: paneWidth, height: graphType === 1 ? 232 : 260, marginTop: -40 }}
              >
                <BackgroundLines
                  getMaxWidth={getMaxWidth}
                  paneWidth={paneWidth}
                  allParticipants={true}
                />

                {graphType !== 1 && <div className={classes.customBottomline} style={{ background: UtilService.refactorGraphColor(graphType !== 1 ? (graphs[0].immersion.overall_index < 2 ? 'gray' : graphs[0].immersion.overall_color_indicator) : (graphs[0].overall_safety.safety_index > 1 ? graphs[0].overall_safety.color_indicator : 'gray')) }} />}
                {graphType !== 1 && <div className={classes.customTopline} style={{ background: UtilService.refactorGraphColor(graphs[0].immersion.overall_index < 2 ? 'gray' : graphs[0].immersion.overall_color_indicator) }} />}
                {graphType === 1 && <div className={classes.customTopline} style={{ background: UtilService.refactorGraphColor(graphs[0].overall_safety.safety_index > 1 ? graphs[0].overall_safety.color_indicator : 'gray') }} />}

                <div
                  className={classes.customTopTitle}
                  style={{
                    color: UtilService.refactorGraphColor(graphType !== 1
                      ? (graphs[0].immersion.overall_color_indicator)
                      : (graphs[0].overall_safety.safety_index > 1 ? graphs[0].overall_safety.color_indicator : 'gray'))
                  }}
                >
                  <p>All Participants</p>
                  <p>{graphs[0].immersion.overall_context_indicator}</p>
                </div>
                {graphType !== 1 && <CoreAnalysisLineBarGraph width={getMaxWidth} playPosition={blueLineWidth} height={paneWidth / 4.5} graphData={graphs[0].immersion} marginTop={41} marginBottom={42} popOut={onSetVideoPlayed} />}
                {graphType !== 1 && <PillSection onPillClicked={onPillClicked} graphWidth={getMaxWidth} graphData={graphs[0]} videoPlayed={stimulus ? (videoPlayed * stimulus.stimulus_video_length) : 0} onZoom={(e: any, t: any) => onZoom(e, t)} popOut={onSetVideoPlayed} marginTop={48} />}
                {graphType === 1 && <CoreAnalysisBarGraph graphData={graphs[0].safety} marginTop={1} marginBottom={0.1} />}
              </div>
            }
            <div style={{ marginTop: (stimulus) ? 0 : -22 }} >
              {graphs.map((graphData: any, index: number) => (
                <Fade key={index}>
                  <div>
                    {graphType !== 1 && <CoreAnalysisLineBarGraph width={getMaxWidth} playPosition={blueLineWidth} height={paneWidth / 4.5} graphData={graphData.immersion} popOut={onSetVideoPlayed} />}
                    {graphType !== 1 && <PillSection onPillClicked={onPillClicked} graphWidth={getMaxWidth} graphData={graphData} videoPlayed={stimulus ? (videoPlayed * stimulus.stimulus_video_length) : 0} onZoom={(e: any, t: any) => onZoom(e, t)} popOut={onSetVideoPlayed} />}
                    {graphType !== 0 && <CoreAnalysisBarGraph graphData={graphData.safety} />}
                  </div>
                </Fade>
              ))}
            </div>
            {session_type !== 'ASYNC' &&
              <AgendaSection
                graphWidth={getMaxWidth}
                graphData={graphs[0]}
                lineStep={15}
                onZoom={(e: any, t: any) => onZoom(e, t)}
              />
            }
            <MarkerSection addNoteX={addNoteX} addNoteY={addNoteY} lineStep={15} />
          </div>

        </ReactCursorPosition>

      </div>

    </>
  )
}